import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import api from '@/lib/api';
import '@/pages/Products.css';

const defaultProductionTimeSettings = {
  enabled: true,
  default_profile_key: 'offset_principal',
  profiles: [
    {
      key: 'offset_principal',
      label: 'Offset principal',
      type: 'offset',
      setup_minutes: 45,
      cost_setup_minutes: 20,
      quantity_variable: 'pliegos',
      sides_multiplier_variable: '',
      default_sides_multiplier: 1,
      process_labor_hour_cost: 0,
      print_units_per_hour: 4000,
      guillotine_units_per_hour: 4000,
      magazine_guillotine_units_per_hour: 400,
      packaging_units_per_hour: 5000,
      magazine_packaging_units_per_hour: 500,
      ink_consumption_g_m2: 1.6,
      ink_startup_waste: 0,
      stapling_setup_minutes: 0,
      stapling_units_per_hour: 250,
      stapling_labor_hour_cost: 0,
      staple_box_price: 90138,
      staples_per_box: 5000,
      staples_per_unit: 2,
      speed_curve: [
        { from_quantity: 1, to_quantity: 500, sheets_per_hour: 2500 },
        { from_quantity: 501, to_quantity: 2000, sheets_per_hour: 4000 },
        { from_quantity: 2001, to_quantity: '', sheets_per_hour: 6000 },
      ],
    },
  ],
};

const normalizeTimeSettingsForForm = (settings) => ({
  ...defaultProductionTimeSettings,
  ...(settings || {}),
  profiles: (settings?.profiles || defaultProductionTimeSettings.profiles).map(profile => ({
    ...defaultProductionTimeSettings.profiles[0],
    ...profile,
    speed_curve: (profile.speed_curve || []).map(segment => ({
      ...segment,
      to_quantity: segment.to_quantity ?? '',
    })),
  })),
});

const ProductionModelsSettings = () => {
  const location = useLocation();
  const [models, setModels] = useState([]);
  const [selectedModelKey, setSelectedModelKey] = useState('offset');
  const [productionTimeSettings, setProductionTimeSettings] = useState(defaultProductionTimeSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const selectedModel = useMemo(
    () => models.find(model => model.key === selectedModelKey) || models[0],
    [models, selectedModelKey],
  );

  const offsetProfile = productionTimeSettings.profiles?.find(profile => profile.type === 'offset')
    || productionTimeSettings.profiles?.[0]
    || defaultProductionTimeSettings.profiles[0];

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [modelsRes, timeRes] = await Promise.all([
        api.get('/production-models'),
        api.get('/settings/production-time'),
      ]);
      const loadedModels = modelsRes.data || [];
      setModels(loadedModels);
      setSelectedModelKey(loadedModels[0]?.key || 'offset');
      setProductionTimeSettings(normalizeTimeSettingsForForm(timeRes.data));
    } catch (err) {
      setError('Error al cargar modelos de produccion');
    } finally {
      setLoading(false);
    }
  };

  const updateOffsetProfile = (patch) => {
    setProductionTimeSettings(current => ({
      ...current,
      profiles: (current.profiles || []).map(profile => (
        profile.type === 'offset' || profile.key === offsetProfile.key
          ? { ...profile, ...patch }
          : profile
      )),
    }));
    setSavedMessage('');
  };

  const updateOffsetSegment = (index, patch) => {
    const speedCurve = [...(offsetProfile.speed_curve || [])];
    speedCurve[index] = { ...speedCurve[index], ...patch };
    updateOffsetProfile({ speed_curve: speedCurve });
  };

  const addOffsetSegment = () => {
    const curve = offsetProfile.speed_curve || [];
    const last = curve[curve.length - 1];
    const nextFrom = Number(last?.to_quantity || last?.from_quantity || 0) + 1;
    updateOffsetProfile({
      speed_curve: [
        ...curve,
        {
          from_quantity: nextFrom || 1,
          to_quantity: '',
          sheets_per_hour: Number(last?.sheets_per_hour) || 3000,
        },
      ],
    });
  };

  const removeOffsetSegment = (index) => {
    const curve = offsetProfile.speed_curve || [];
    if (curve.length <= 1) {
      setError('La curva necesita al menos un tramo');
      return;
    }
    updateOffsetProfile({ speed_curve: curve.filter((_, idx) => idx !== index) });
  };

  const serializeProductionTimeSettings = () => ({
    enabled: Boolean(productionTimeSettings.enabled),
    default_profile_key: productionTimeSettings.default_profile_key || offsetProfile.key || 'offset_principal',
    profiles: (productionTimeSettings.profiles || []).map(profile => ({
      key: profile.key || 'offset_principal',
      label: profile.label || 'Offset principal',
      type: profile.type || 'offset',
      setup_minutes: Number(profile.setup_minutes) || 0,
      cost_setup_minutes: Number(profile.cost_setup_minutes) || 20,
      quantity_variable: profile.quantity_variable || 'pliegos',
      sides_multiplier_variable: profile.sides_multiplier_variable || '',
      default_sides_multiplier: Number(profile.default_sides_multiplier) || 1,
      process_labor_hour_cost: Number(profile.process_labor_hour_cost) || 0,
      print_units_per_hour: Number(profile.print_units_per_hour) || 4000,
      guillotine_units_per_hour: Number(profile.guillotine_units_per_hour) || 4000,
      magazine_guillotine_units_per_hour: Number(profile.magazine_guillotine_units_per_hour) || 400,
      packaging_units_per_hour: Number(profile.packaging_units_per_hour) || 5000,
      magazine_packaging_units_per_hour: Number(profile.magazine_packaging_units_per_hour) || 500,
      ink_consumption_g_m2: Number(profile.ink_consumption_g_m2) || 1.6,
      ink_startup_waste: Number(profile.ink_startup_waste) || 0,
      stapling_setup_minutes: Number(profile.stapling_setup_minutes) || 0,
      stapling_units_per_hour: Number(profile.stapling_units_per_hour) || 250,
      stapling_labor_hour_cost: Number(profile.stapling_labor_hour_cost) || 0,
      staple_box_price: Number(profile.staple_box_price) || 90138,
      staples_per_box: Number(profile.staples_per_box) || 5000,
      staples_per_unit: Number(profile.staples_per_unit) || 2,
      speed_curve: (profile.speed_curve || []).map(segment => ({
        from_quantity: Number(segment.from_quantity) || 1,
        to_quantity: segment.to_quantity === '' || segment.to_quantity === null ? null : Number(segment.to_quantity),
        sheets_per_hour: Number(segment.sheets_per_hour) || 0,
      })),
    })),
  });

  const handleSaveOffset = async () => {
    setError('');
    setSavedMessage('');
    setSaving(true);
    try {
      const { data } = await api.put('/settings/production-time', serializeProductionTimeSettings());
      setProductionTimeSettings(normalizeTimeSettingsForForm(data));
      setSavedMessage('Modelo Offset guardado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al guardar el modelo Offset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />
      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1>Modelos</h1>
            <p className="dashboard-subtitle">
              Configuracion separada por tipo de produccion.
            </p>
          </div>
          <button className="btn-secondary" onClick={load} disabled={loading}>
            Actualizar
          </button>
        </div>

        {error && <div className="login-error">{error}</div>}
        {savedMessage && <div className="success-message">{savedMessage}</div>}

        <div className="card editor-card">
          <div className="production-model-tabs">
            {(models.length ? models : [{ key: 'offset', label: 'Offset', implemented: true }]).map(model => (
              <button
                key={model.key}
                type="button"
                className={`production-model-tab ${selectedModelKey === model.key ? 'active' : ''}`}
                onClick={() => setSelectedModelKey(model.key)}
              >
                <span>{model.label}</span>
                {!model.implemented && <small>Pendiente</small>}
              </button>
            ))}
          </div>
        </div>

        {selectedModel?.key === 'offset' ? (
          <div className="card editor-card">
            <div className="card-title-row">
              <div>
                <h3 className="card-title-inline">Offset</h3>
                <p className="dashboard-subtitle">
                  Imposicion de pliego y curva de velocidad por cantidad de pliegos.
                </p>
              </div>
              <button className="btn-primary-inline" onClick={handleSaveOffset} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Offset'}
              </button>
            </div>

            <div className="variable-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={productionTimeSettings.enabled}
                    onChange={(e) => setProductionTimeSettings({
                      ...productionTimeSettings,
                      enabled: e.target.checked,
                    })}
                  />
                  {' '}Activar estimacion de tiempo
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Nombre del perfil</label>
                <input
                  className="form-input"
                  value={offsetProfile.label}
                  onChange={(e) => updateOffsetProfile({ label: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Preparacion fija (min)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.setup_minutes}
                  onChange={(e) => updateOffsetProfile({ setup_minutes: e.target.value })}
                  step="1"
                />
              </div>
            </div>

            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Variable de cantidad</label>
                <input
                  className="form-input mono"
                  value={offsetProfile.quantity_variable}
                  onChange={(e) => updateOffsetProfile({ quantity_variable: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                  placeholder="pliegos"
                />
                <div className="input-hint">En Offset normalmente se usa la variable automatica: pliegos.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Multiplicador por defecto</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.default_sides_multiplier}
                  onChange={(e) => updateOffsetProfile({ default_sides_multiplier: e.target.value })}
                  step="1"
                />
                <div className="input-hint">Usa 2 para doble pasada si no hay una variable especifica.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Variable multiplicadora opcional</label>
                <input
                  className="form-input mono"
                  value={offsetProfile.sides_multiplier_variable || ''}
                  onChange={(e) => updateOffsetProfile({ sides_multiplier_variable: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                  placeholder="caras"
                />
              </div>
            </div>

            <div className="form-label" style={{ marginTop: '1rem' }}>Costos de procesos</div>
            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Preparacion costo (min)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.cost_setup_minutes ?? 20}
                  onChange={(e) => updateOffsetProfile({ cost_setup_minutes: e.target.value })}
                  step="1"
                />
                <div className="input-hint">Minutos usados para la variable preparacion del precio.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Costo mano de obra procesos ($/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.process_labor_hour_cost ?? 0}
                  onChange={(e) => updateOffsetProfile({ process_labor_hour_cost: e.target.value })}
                  step="1"
                />
                <div className="input-hint">Si queda en 0 usa la variable global horahombre.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Velocidad impresion (pliegos/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.print_units_per_hour ?? 4000}
                  onChange={(e) => updateOffsetProfile({ print_units_per_hour: e.target.value })}
                  step="1"
                />
              </div>
            </div>

            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Velocidad guillotina general (u/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.guillotine_units_per_hour ?? 4000}
                  onChange={(e) => updateOffsetProfile({ guillotine_units_per_hour: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Velocidad guillotina revista (u/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.magazine_guillotine_units_per_hour ?? 400}
                  onChange={(e) => updateOffsetProfile({ magazine_guillotine_units_per_hour: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Velocidad empaquetado general (u/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.packaging_units_per_hour ?? 5000}
                  onChange={(e) => updateOffsetProfile({ packaging_units_per_hour: e.target.value })}
                  step="1"
                />
              </div>
            </div>

            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Velocidad empaquetado revista (u/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.magazine_packaging_units_per_hour ?? 500}
                  onChange={(e) => updateOffsetProfile({ magazine_packaging_units_per_hour: e.target.value })}
                  step="1"
                />
              </div>
            </div>

            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Consumo tinta base (g/m2)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.ink_consumption_g_m2 ?? 1.6}
                  onChange={(e) => updateOffsetProfile({ ink_consumption_g_m2: e.target.value })}
                  step="0.1"
                />
                <div className="input-hint">Valor al 100% de cobertura. Por defecto: 1.6 g/m2.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Merma arranque tinta</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.ink_startup_waste ?? 0}
                  onChange={(e) => updateOffsetProfile({ ink_startup_waste: e.target.value })}
                  step="0.01"
                />
                <div className="input-hint">Usa 0.05 para 5% adicional.</div>
              </div>
            </div>

            <div className="form-label" style={{ marginTop: '1rem' }}>Abrochado</div>
            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Preparacion abrochado (min)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.stapling_setup_minutes ?? 0}
                  onChange={(e) => updateOffsetProfile({ stapling_setup_minutes: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Velocidad abrochado (u/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.stapling_units_per_hour ?? 250}
                  onChange={(e) => updateOffsetProfile({ stapling_units_per_hour: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mano de obra abrochado ($/h)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.stapling_labor_hour_cost ?? 0}
                  onChange={(e) => updateOffsetProfile({ stapling_labor_hour_cost: e.target.value })}
                  step="1"
                />
              </div>
            </div>

            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Precio caja broches ($)</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.staple_box_price ?? 90138}
                  onChange={(e) => updateOffsetProfile({ staple_box_price: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Broches por caja</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.staples_per_box ?? 5000}
                  onChange={(e) => updateOffsetProfile({ staples_per_box: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Broches por unidad</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={offsetProfile.staples_per_unit ?? 2}
                  onChange={(e) => updateOffsetProfile({ staples_per_unit: e.target.value })}
                  step="1"
                />
              </div>
            </div>

            <div className="form-label" style={{ marginTop: '1rem' }}>Curva de velocidad por rango</div>
            <div className="production-speed-header">
              <span>Desde pliegos</span>
              <span>Hasta pliegos</span>
              <span>Pliegos por hora</span>
              <span></span>
            </div>
            {(offsetProfile.speed_curve || []).map((segment, idx) => (
              <div className="option-row production-speed-row" key={idx}>
                <input
                  type="number"
                  className="form-input mono"
                  value={segment.from_quantity}
                  onChange={(e) => updateOffsetSegment(idx, { from_quantity: e.target.value })}
                  placeholder="Desde"
                />
                <input
                  type="number"
                  className="form-input mono"
                  value={segment.to_quantity}
                  onChange={(e) => updateOffsetSegment(idx, { to_quantity: e.target.value })}
                  placeholder="Hasta"
                />
                <input
                  type="number"
                  className="form-input mono"
                  value={segment.sheets_per_hour}
                  onChange={(e) => updateOffsetSegment(idx, { sheets_per_hour: e.target.value })}
                  placeholder="Ej: 3000"
                />
                <button className="btn-remove" onClick={() => removeOffsetSegment(idx)}>x</button>
              </div>
            ))}
            <div className="input-hint">
              Ejemplo: 1000 pliegos usa el tramo donde 1000 cae entre Desde y Hasta; si ese tramo dice 3000 pliegos/hora, la tirada se calcula con esa velocidad.
            </div>
            <button className="btn-small" onClick={addOffsetSegment}>+ Tramo</button>
          </div>
        ) : (
          <div className="card editor-card">
            <h3 className="card-title">{selectedModel?.label || 'Modelo'}</h3>
            <p className="dashboard-subtitle">{selectedModel?.description}</p>
            <div className="empty-state">
              Este modelo esta preparado en la arquitectura, pero su calculador todavia no esta implementado.
              Cuando se agregue, tendra sus propias variables, configuraciones y reglas sin tocar Offset.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductionModelsSettings;
